// The real count. One POST to the GraphQL API (no auth; CORS is open for
// POST /graphql), rendered into every [data-impact-minutes] on the page —
// the purpose band and the footer line share this fetch. Until data arrives,
// and if the request fails, the number stays "—" and the small stats stay
// hidden: never a placeholder or estimated number. Refreshes every minute
// while the tab is visible. The big number counts up the first time its
// block scrolls into view (instantly under prefers-reduced-motion).
const source = document.querySelector("[data-impact-url]")
const url = source?.dataset.impactUrl

if (url) {
  const QUERY = "{ impact { awaySeconds missionsCompleted familiesReached teachers since } }"
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const numberFormat = new Intl.NumberFormat("en-US")
  const monthFormat = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" })
  const FIELDS = {
    minutes: "[data-impact-minutes]",
    missionsCompleted: "[data-impact-missions]",
    familiesReached: "[data-impact-families]",
    teachers: "[data-impact-teachers]",
  }
  const shown = new WeakMap() // element -> number currently displayed
  const raf = new WeakMap() // element -> running animation frame
  const onScreen = new WeakSet() // [data-impact-block] elements that have scrolled into view
  let data = null

  const setNumber = (el, target) => {
    const from = shown.get(el) ?? 0
    shown.set(el, target)
    cancelAnimationFrame(raf.get(el))
    if (reduced || from === target || !el.hasAttribute("data-impact-animate")) {
      el.textContent = numberFormat.format(target)
      return
    }
    const start = performance.now()
    const duration = 1100
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3)
      el.textContent = numberFormat.format(Math.round(from + (target - from) * eased))
      if (t < 1) raf.set(el, requestAnimationFrame(step))
    }
    raf.set(el, requestAnimationFrame(step))
  }

  const blockOf = (el) => el.closest("[data-impact-block]")
  const ready = (el) => {
    const block = blockOf(el)
    return !block || onScreen.has(block)
  }

  function render() {
    if (!data) return
    const values = {
      minutes: Math.round(data.awaySeconds / 60),
      missionsCompleted: data.missionsCompleted,
      familiesReached: data.familiesReached,
      teachers: data.teachers,
    }
    for (const [key, selector] of Object.entries(FIELDS)) {
      document.querySelectorAll(selector).forEach((el) => {
        if (typeof values[key] !== "number" || !ready(el)) return
        setNumber(el, values[key])
      })
    }
    document.querySelectorAll("[data-impact-stats]").forEach((el) => {
      if (ready(el)) el.hidden = false
    })
    if (data.since) {
      const date = new Date(data.since)
      if (!Number.isNaN(date.getTime())) {
        document.querySelectorAll("[data-impact-since]").forEach((el) => {
          el.textContent = `Since ${monthFormat.format(date)}.`
          el.hidden = false
        })
      }
    }
  }

  async function load() {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ query: QUERY }),
      })
      if (!res.ok) return
      const json = await res.json()
      const impact = json?.data?.impact
      if (!impact || typeof impact.awaySeconds !== "number") return
      data = impact
      render()
    } catch {
      // Leave the dashes. A failed fetch must never become a number.
    }
  }

  const blocks = document.querySelectorAll("[data-impact-block]")
  if ("IntersectionObserver" in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          onScreen.add(entry.target)
          io.unobserve(entry.target)
        })
        render()
      },
      { threshold: 0.25 }
    )
    blocks.forEach((block) => io.observe(block))
  } else {
    blocks.forEach((block) => onScreen.add(block))
  }

  load()
  setInterval(() => {
    if (document.visibilityState === "visible") load()
  }, 60_000)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") load()
  })
}
